import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import LoadingIndicator from "flarum/common/components/LoadingIndicator";
import classList from "flarum/common/utils/classList";
import ChatAutocomplete from "./ChatAutocomplete";
import { searchEmoji } from "../utils/emoji";
import { messagePreview } from "../../common/utils/preview";
import { humanDuration } from "../utils/duration";
import { resolveMaxMessageLength } from "../utils/messageLimit";
import { sendsOnCtrlEnter } from "../utils/shortcuts";
import MessageTooLongModal from "./MessageTooLongModal";
import { authorName } from "../utils/bot";
import { acceptInvitation, adoptChannelPayload, declineInvitation, invitationErrorText } from "../utils/invitations";
import { stickersAvailable, stickerIcon, stickerLabel, openStickerPicker, close as closeStickerPicker, isStickerPickerOpen } from "../utils/stickers";
import { flamojiPickerButton, loadCustomEmoji, searchCustomEmoji, customEmojiImage } from "../utils/flamoji";
import iconLabel from "../utils/iconLabel";
/**
 * The message input.
 *
 * Auto-grows to a cap, persists a draft, throttles typing announcements, and
 * submits on Enter with Shift+Enter for a newline — the conventions a chat user
 * arrives already expecting.
 */
/**
 * How long to wait after a keystroke before asking the server for matching users.
 * Emoji are matched locally and need no delay.
 */
const MENTION_DEBOUNCE = 180;
export default class ChatComposer extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "textarea", null);
    _defineProperty(this, "joining", false);
    _defineProperty(this, "sending", false);
    /** Seconds left before this channel will accept another message from us. */
    _defineProperty(this, "cooldown", 0);
    _defineProperty(this, "cooldownTimer", null);
    /** Last `slowModeRemaining` seen, so a change can be told from a redraw. */
    _defineProperty(this, "lastServerCooldown", -1);
    /** Last `slowModeSeconds` seen. -1 so the first sync always runs. */
    _defineProperty(this, "lastSlowModeWindow", -1);
    _defineProperty(this, "uploading", false);
    /** Id of the reply/edit target the cursor was last moved for. */
    _defineProperty(this, "focusedContext", null);
    // ── Autocomplete ───────────────────────────────────────────────────────────
    _defineProperty(this, "suggestions", []);
    _defineProperty(this, "activeSuggestion", 0);
    /** The `@foo` / `:smi` fragment being completed, as [start, end) in the value. */
    _defineProperty(this, "trigger", null);
    _defineProperty(this, "mentionTimer", null);
    /** Discards a slower earlier user search whose results arrived out of order. */
    _defineProperty(this, "mentionSequence", 0);
  }
  onremove() {
    if (this.mentionTimer !== null) window.clearTimeout(this.mentionTimer);
    this.stopCooldown();
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.syncSlowMode();

    // Warm the custom-emoji set so the `:` list and the picker are complete on
    // first use. A no-op when Flamoji is not installed.
    loadCustomEmoji();
  }

  /**
   * Reconciles the local countdown with the channel's slow-mode rule.
   *
   * Both directions matter, and they are not symmetrical.
   *
   * Turning slow mode *on* is the server's word to adopt: `slowModeRemaining`
   * says how long this actor has left, and an already-open composer has to pick
   * it up or the new rule binds them only after they navigate away and back.
   * It is read on a *change* rather than every draw — it is a snapshot from the
   * last channel read, not a ticking value, so comparing it each time would
   * restart the countdown from the same stale number and it would never reach
   * zero.
   *
   * Turning slow mode *off* has to release whoever is mid-wait. The local
   * countdown exists only because of the window it was started from, so it
   * cannot outlive that window: with no window there is nothing left to wait
   * for, and a narrowed one caps what is left. This is the half that was
   * missing — a "never shorten" guard meant to protect the local countdown from
   * a stale snapshot also kept enforcing a rule that had just been withdrawn.
   *
   * Narrowing is capped rather than recomputed: the broadcast moves the window
   * immediately while the per-actor refetch is still in flight, so `remaining`
   * is briefly stale. Capping at the new window is never more permissive than
   * the server, and the next send resyncs it exactly.
   */
  syncSlowMode() {
    var _channel$slowModeSeco, _channel$slowModeRema;
    const channel = this.attrs.channel;
    const window = Number((_channel$slowModeSeco = channel.slowModeSeconds()) != null ? _channel$slowModeSeco : 0);
    const remaining = Number((_channel$slowModeRema = channel.slowModeRemaining()) != null ? _channel$slowModeRema : 0);
    if (window === this.lastSlowModeWindow && remaining === this.lastServerCooldown) {
      return;
    }
    const windowChanged = window !== this.lastSlowModeWindow;
    this.lastSlowModeWindow = window;
    this.lastServerCooldown = remaining;
    if (windowChanged && this.cooldown > window) {
      if (window <= 0) {
        this.stopCooldown();
      } else {
        this.startCooldown(window);
      }
      return;
    }

    // Capped at the window for the same reason it is capped above: while the
    // refetch is in flight `remaining` still describes the *old* rule, and a
    // window that was just narrowed must not be lengthened by a stale figure.
    // The server can never require longer than the window itself.
    const wait = Math.min(remaining, window);
    if (wait > this.cooldown) {
      this.startCooldown(wait);
    }
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.textarea = vnode.dom.querySelector(".ChatComposer-input");
    this.resize();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.syncSlowMode();
    this.focusOnNewContext();
  }

  /**
   * Puts the cursor in the input when a reply or edit is staged.
   *
   * Handled here rather than at each call site because a reply can be started from
   * three places — the channel's message row, the thread panel, and arrow-up — and
   * only the composer knows where its own textarea is. Clicking Reply and then
   * having to click the box before typing is a small thing that happens on every
   * single reply.
   *
   * Fires only on a *change* of target: `onupdate` runs on every redraw, including
   * one per incoming message, and focusing on each of those would seize the cursor
   * from someone reading, or scroll a phone's keyboard open unprompted.
   */
  focusOnNewContext() {
    var _this$editing;
    const target = (_this$editing = this.editing()) != null ? _this$editing : this.replyingTo();
    const id = target ? String(target.id()) : null;
    if (id === this.focusedContext) return;
    this.focusedContext = id;
    if (!id || !this.textarea) return;
    this.textarea.focus();

    // Caret at the end. An edit pre-fills the box with the existing text, and
    // landing at position zero means typing inserts before it.
    const end = this.textarea.value.length;
    this.textarea.setSelectionRange(end, end);
  }
  view() {
    const _this$attrs = this.attrs,
      channel = _this$attrs.channel,
      state = _this$attrs.state,
      threadId = _this$attrs.threadId;
    if (!channel.canPostMessage()) {
      return this.frozen(channel);
    }

    // Slow mode is a "not now", so it reads like the other "not now" states
    // rather than as a disabled button with a number in it. A greyed-out send
    // still invites clicking, and a textarea that accepts typing it will not
    // deliver is worse than no textarea: the draft survives in state either way,
    // so nothing is lost by standing the box down until the wait is over.
    if (this.cooldown > 0) {
      return this.slowed();
    }
    const value = state.draft(Number(channel.id()), threadId != null ? threadId : null);
    const max = resolveMaxMessageLength(channel);
    const remaining = max - value.length;
    return m("div", {
      className: "ChatComposer"
    }, this.contextBar(), this.pendingAttachments(), m(ChatAutocomplete, {
      suggestions: this.suggestions,
      activeIndex: this.activeSuggestion,
      onSelect: suggestion => this.applySuggestion(suggestion),
      onHover: index => {
        this.activeSuggestion = index;
      }
    }), m("div", {
      className: "ChatComposer-shell"
    }, m("textarea", {
      className: "ChatComposer-input",
      placeholder: this.placeholder(channel),
      value: value,
      rows: 1
      // Deliberately no `maxlength`: the browser enforces it by silently
      // dropping the tail of a paste, so a long quote arrives cut with no
      // sign that anything went missing. Over-length text is allowed into
      // the box, the counter goes negative, and `submit()` explains.
      ,
      oninput: e => this.onInput(e),
      onkeydown: e => this.onKeyDown(e),
      disabled: this.sending,
      "aria-label": this.placeholder(channel)
    }), m("div", {
      className: "ChatComposer-bar"
    }, remaining <= 200 ? m("div", {
      className: classList("ChatComposer-counter", {
        "ChatComposer-counter--warning": remaining <= 20 && remaining >= 0,
        "ChatComposer-counter--over": remaining < 0
      }),
      "aria-live": "polite"
    }, remaining) : null, m("div", {
      className: "ChatComposer-tools"
    }, app.forum.attribute("ramon-chat.allowUploads") && app.forum.attribute("canUploadChatFiles") ? m('[', null, m("input", {
      type: "file",
      className: "ChatComposer-fileInput",
      style: {
        display: "none"
      },
      multiple: true,
      onchange: e => this.onFilesPicked(e)
    }), m(Button, Object.assign({
      className: "ChatComposer-tool",
      icon: "fas fa-paperclip"
    }, iconLabel(app.translator.trans("ramon-chat.forum.composer.attach", {}, true)), {
      disabled: this.uploading || this.sending,
      onclick: () => this.pickFiles()
    }))) : null, stickersAvailable() ?
    // A plain button, not `Button` with an `icon` attr: that attr takes a
    // Font Awesome class name, and this icon is an inline SVG so it
    // matches the one on the discussion composer exactly.
    m("button", {
      type: "button",
      className: "ChatComposer-tool",
      title: stickerLabel(),
      "aria-label": stickerLabel(),
      disabled: this.sending,
      onclick: e => this.toggleStickers(e)
    }, stickerIcon()) : null, flamojiPickerButton(text => this.insertAtCursor(text), this.sending)), m("button", {
      type: "button",
      className: "ChatComposer-send"
      // Names the keystroke, since which one sends is a forum setting and
      // the button is the only place a member would find out.
      ,
      title: this.sendHint(),
      disabled: this.sending || !value.trim() && state.pendingUploads.length === 0,
      onclick: () => this.submit()
    }, this.sending ? m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }) : m("i", {
      className: "fas fa-paper-plane"
    })))));
  }

  /**
   * Why you cannot type here.
   *
   * The reasons are checked most-specific first, because they are not equivalent:
   * "this channel is closed" told someone in a perfectly open announcement channel
   * the wrong thing entirely, and left them with no idea that reading was all that
   * was ever on offer.
   */
  /**
   * What stands in for the composer during a slow-mode wait.
   *
   * Shaped like `frozen()` because it means the same thing to the reader — the
   * channel is not taking a message from you right now — and an hourglass rather
   * than a padlock because nothing is locked and nothing went wrong: the wait
   * ends on its own, and the count says when.
   */
  slowed() {
    return m("div", {
      className: "ChatChannel-frozen ChatChannel-frozen--slow"
    }, m("i", {
      className: "fas fa-hourglass-half",
      "aria-hidden": "true"
    }), m("span", null, app.translator.trans("ramon-chat.forum.composer.slow_mode_wait", {
      duration: humanDuration(this.cooldown)
    })));
  }
  frozen(channel) {
    // Invited but not in. The first thing to check: an invitation is the one
    // state where the reader has been asked a question, and the answer is what
    // this bar is for.
    if (!channel.isFollowing() && channel.isInvited() && channel.isOpen()) {
      const inviter = channel.invitedByName();
      return m("div", {
        className: "ChatChannel-frozen ChatChannel-frozen--join ChatChannel-frozen--invited"
      }, m("i", {
        className: "fas fa-envelope-open",
        "aria-hidden": "true"
      }), m("span", null, inviter ? app.translator.trans("ramon-chat.forum.channel.invited_by", {
        username: inviter
      }) : app.translator.trans("ramon-chat.forum.channel.invited")), m("span", {
        className: "ChatChannel-frozen-actions"
      }, m(Button, {
        className: "Button",
        disabled: this.joining,
        onclick: () => this.answerInvitation(channel, false)
      }, app.translator.trans("ramon-chat.forum.channel.decline_invite")), m(Button, {
        className: "Button Button--primary",
        icon: "fas fa-check",
        loading: this.joining,
        onclick: () => this.answerInvitation(channel, true)
      }, app.translator.trans("ramon-chat.forum.channel.accept_invite"))));
    }

    // Not a member. Checked before the read-only reasons because it is the only one
    // the reader can do something about, and telling them the channel is closed when
    // it is merely unjoined would be the same mistake as the announcement case.
    if (!channel.isArchived() && !channel.isClosed() && channel.postPermission() !== "moderators" && !channel.isFollowing() && channel.canJoin()) {
      return m("div", {
        className: "ChatChannel-frozen ChatChannel-frozen--join"
      }, m("span", null, app.translator.trans("ramon-chat.forum.channel.join_to_post")), m(Button, {
        className: "Button Button--primary",
        icon: "fas fa-right-to-bracket",
        loading: this.joining,
        onclick: () => this.join(channel)
      }, app.translator.trans("ramon-chat.forum.channel.join")));
    }
    const key = channel.isArchived() ? "ramon-chat.forum.channel.archived" : channel.isClosed() ? "ramon-chat.forum.channel.closed" : channel.postPermission() === "moderators" ? "ramon-chat.forum.channel.moderators_only" : "ramon-chat.forum.composer.placeholder_closed";

    // A megaphone, not a padlock: an announcement channel is not locked, it is
    // read-only by design, and the lock icon reads as something having gone wrong.
    const icon = key.endsWith("moderators_only") ? "fas fa-bullhorn" : "fas fa-lock";
    return m("div", {
      className: "ChatChannel-frozen"
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), m("span", null, app.translator.trans(key)));
  }

  /**
   * Joins the channel so the composer can appear.
   *
   * The join answers with the channel itself, so `canPostMessage` is the
   * server's answer rather than an assumption: a join that succeeded for a
   * channel that has since been closed still leaves the composer hidden, and
   * one that succeeded for an open channel draws it without a second request.
   */
  async join(channel) {
    this.joining = true;
    m.redraw();
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-channels/").concat(channel.id(), "/join"),
        body: {
          data: {
            attributes: {}
          }
        }
      });
      if (!adoptChannelPayload(payload)) {
        channel.pushAttributes({
          isFollowing: true,
          canPostMessage: channel.isOpen()
        });
      }
      this.attrs.state.rememberChannel(channel);
    } catch (e) {
      var _e$response$errors$0$, _e$response;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$ = e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) != null ? _e$response$errors$0$ : app.translator.trans("ramon-chat.forum.channel.join_failed"));
    } finally {
      this.joining = false;
      m.redraw();
    }
  }

  /**
   * Answers the invitation the bar above is showing.
   *
   * Accepting is a join that goes through the invite route, and lands the same
   * way: the returned record carries `canPostMessage`, so the composer takes
   * the bar's place on the next draw.
   */
  async answerInvitation(channel, accept) {
    if (!accept && !confirm(app.translator.trans("ramon-chat.forum.channel.decline_invite_confirm", {}, true))) {
      return;
    }
    this.joining = true;
    m.redraw();
    try {
      if (accept) {
        const joined = await acceptInvitation(Number(channel.id()));
        if (joined) this.attrs.state.rememberChannel(joined);
      } else {
        await declineInvitation(Number(channel.id()));
      }
      app.alerts.show({
        type: "success"
      }, app.translator.trans(accept ? "ramon-chat.forum.channel.invite_accepted" : "ramon-chat.forum.channel.invite_declined"));
    } catch (e) {
      app.alerts.show({
        type: "error"
      }, invitationErrorText(e, "ramon-chat.forum.channel.invite_failed"));
    } finally {
      this.joining = false;
      m.redraw();
    }
  }

  /**
   * This composer's own reply/edit target.
   *
   * The channel and an open thread each render a composer over the same state, so
   * the context is looked up by scope — a thread edit must not put the channel
   * composer into edit mode.
   */
  replyingTo() {
    var _this$attrs$threadId;
    return this.attrs.state.replyingTo(Number(this.attrs.channel.id()), (_this$attrs$threadId = this.attrs.threadId) != null ? _this$attrs$threadId : null);
  }

  /** Whether the staged reply was started as "reply in thread". */
  branching() {
    var _this$attrs$threadId2;
    return this.attrs.state.branchingFrom(Number(this.attrs.channel.id()), (_this$attrs$threadId2 = this.attrs.threadId) != null ? _this$attrs$threadId2 : null);
  }
  editing() {
    var _this$attrs$threadId3;
    return this.attrs.state.editing(Number(this.attrs.channel.id()), (_this$attrs$threadId3 = this.attrs.threadId) != null ? _this$attrs$threadId3 : null);
  }
  placeholder(channel) {
    if (this.attrs.threadId) {
      return app.translator.trans("ramon-chat.forum.composer.placeholder_thread", {}, true);
    }
    return app.translator.trans("ramon-chat.forum.composer.placeholder", {
      channel: channel.displayName()
    }, true);
  }

  /** The send button's tooltip, naming the keystroke that actually sends. */
  sendHint() {
    return app.translator.trans(sendsOnCtrlEnter() ? "ramon-chat.forum.composer.send_ctrl_enter" : "ramon-chat.forum.composer.send_enter", {}, true);
  }

  /** Reply / edit context strip above the input. */
  contextBar() {
    const editingTarget = this.editing();
    const target = editingTarget != null ? editingTarget : this.replyingTo();
    if (!target) return null;
    const editing = Boolean(editingTarget);

    // A branch and a reply stage identically, and now send differently — so the
    // strip has to say which one is about to happen. Same icon the thread action
    // uses, so the two read as the same feature.
    const branching = !editing && this.branching();
    return m("div", {
      className: "ChatComposer-context"
    }, m("i", {
      className: classList({
        "fas fa-pencil": editing,
        "fas fa-comments": branching,
        "fas fa-reply": !editing && !branching
      }),
      "aria-hidden": "true"
    }), m("span", {
      className: "ChatComposer-context-label"
    }, editing ? app.translator.trans("ramon-chat.forum.composer.editing") : app.translator.trans(branching ? "ramon-chat.forum.message.starting_thread" : "ramon-chat.forum.message.replying_to", {
      username: authorName(target)
    })), m("span", {
      className: "ChatComposer-context-preview"
    }, messagePreview(target, 80)), m(Button, Object.assign({
      className: "ChatComposer-tool",
      icon: "fas fa-times"
    }, iconLabel(app.translator.trans("ramon-chat.forum.composer.cancel_edit", {}, true)), {
      onclick: () => this.cancelContext()
    })));
  }
  pendingAttachments() {
    const state = this.attrs.state;
    if (state.pendingUploads.length === 0 && !this.uploading) return null;
    return m("div", {
      className: "ChatComposer-pending"
    }, state.pendingUploads.map(upload => m("div", {
      key: upload.id(),
      className: "ChatUploads-file"
    }, m("i", {
      className: "fas fa-paperclip",
      "aria-hidden": "true"
    }), m("span", {
      className: "ChatUploads-file-name"
    }, upload.fileName()), m(Button, Object.assign({
      className: "ChatComposer-tool",
      icon: "fas fa-times"
    }, iconLabel(app.translator.trans("ramon-chat.forum.composer.remove_upload", {}, true)), {
      onclick: () => this.removeUpload(Number(upload.id()))
    })))), this.uploading ? m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }) : null);
  }

  // ── Input handling ─────────────────────────────────────────────────────────

  onInput(e) {
    const target = e.target;
    const _this$attrs2 = this.attrs,
      channel = _this$attrs2.channel,
      state = _this$attrs2.state,
      threadId = _this$attrs2.threadId;
    state.setDraft(Number(channel.id()), target.value, threadId != null ? threadId : null);
    state.announceTyping(Number(channel.id()));
    this.detectTrigger(target);
    this.resize();
  }

  // ── Autocomplete ───────────────────────────────────────────────────────────

  /**
   * Looks for an `@name` or `:emoji` fragment immediately before the caret.
   *
   * Anchored on a word boundary so an email address or a `http://` does not open
   * the list, and closed as soon as the fragment stops matching — the trigger has
   * to disappear on its own rather than needing an explicit dismissal.
   */
  detectTrigger(el) {
    var _el$selectionStart;
    const caret = (_el$selectionStart = el.selectionStart) != null ? _el$selectionStart : 0;
    const before = el.value.slice(0, caret);
    const match = /(^|[\t-\r \(\xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF])([:@])((?:[\+\x2D0-9A-Z_a-z\xAA\xB2\xB3\xB5\xB9\xBA\xBC-\xBE\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0560-\u0588\u05D0-\u05EA\u05EF-\u05F2\u0620-\u064A\u0660-\u0669\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07C0-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u0860-\u086A\u0870-\u0887\u0889-\u088F\u08A0-\u08C9\u0904-\u0939\u093D\u0950\u0958-\u0961\u0966-\u096F\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09E6-\u09F1\u09F4-\u09F9\u09FC\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A66-\u0A6F\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AE6-\u0AEF\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B66-\u0B6F\u0B71-\u0B77\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0BE6-\u0BF2\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C5C\u0C5D\u0C60\u0C61\u0C66-\u0C6F\u0C78-\u0C7E\u0C80\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDC-\u0CDE\u0CE0\u0CE1\u0CE6-\u0CEF\u0CF1\u0CF2\u0D04-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D54-\u0D56\u0D58-\u0D61\u0D66-\u0D78\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0DE6-\u0DEF\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E50-\u0E59\u0E81\u0E82\u0E84\u0E86-\u0E8A\u0E8C-\u0EA3\u0EA5\u0EA7-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0ED0-\u0ED9\u0EDC-\u0EDF\u0F00\u0F20-\u0F33\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F-\u1049\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u1090-\u1099\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1369-\u137C\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16EE-\u16F8\u1700-\u1711\u171F-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u17E0-\u17E9\u17F0-\u17F9\u1810-\u1819\u1820-\u1878\u1880-\u1884\u1887-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1946-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u19D0-\u19DA\u1A00-\u1A16\u1A20-\u1A54\u1A80-\u1A89\u1A90-\u1A99\u1AA7\u1B05-\u1B33\u1B45-\u1B4C\u1B50-\u1B59\u1B83-\u1BA0\u1BAE-\u1BE5\u1C00-\u1C23\u1C40-\u1C49\u1C4D-\u1C7D\u1C80-\u1C8A\u1C90-\u1CBA\u1CBD-\u1CBF\u1CE9-\u1CEC\u1CEE-\u1CF3\u1CF5\u1CF6\u1CFA\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2070\u2071\u2074-\u2079\u207F-\u2089\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2150-\u2189\u2460-\u249B\u24EA-\u24FF\u2776-\u2793\u2C00-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2CFD\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005-\u3007\u3021-\u3029\u3031-\u3035\u3038-\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312F\u3131-\u318E\u3192-\u3195\u31A0-\u31BF\u31F0-\u31FF\u3220-\u3229\u3248-\u324F\u3251-\u325F\u3280-\u3289\u32B1-\u32BF\u3400-\u4DBF\u4E00-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6EF\uA717-\uA71F\uA722-\uA788\uA78B-\uA7DC\uA7F1-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA830-\uA835\uA840-\uA873\uA882-\uA8B3\uA8D0-\uA8D9\uA8F2-\uA8F7\uA8FB\uA8FD\uA8FE\uA900-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF-\uA9D9\uA9E0-\uA9E4\uA9E6-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA50-\uAA59\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB69\uAB70-\uABE2\uABF0-\uABF9\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]|\uD800[\uDC00-\uDC0B\uDC0D-\uDC26\uDC28-\uDC3A\uDC3C\uDC3D\uDC3F-\uDC4D\uDC50-\uDC5D\uDC80-\uDCFA\uDD07-\uDD33\uDD40-\uDD78\uDD8A\uDD8B\uDE80-\uDE9C\uDEA0-\uDED0\uDEE1-\uDEFB\uDF00-\uDF23\uDF2D-\uDF4A\uDF50-\uDF75\uDF80-\uDF9D\uDFA0-\uDFC3\uDFC8-\uDFCF\uDFD1-\uDFD5]|\uD801[\uDC00-\uDC9D\uDCA0-\uDCA9\uDCB0-\uDCD3\uDCD8-\uDCFB\uDD00-\uDD27\uDD30-\uDD63\uDD70-\uDD7A\uDD7C-\uDD8A\uDD8C-\uDD92\uDD94\uDD95\uDD97-\uDDA1\uDDA3-\uDDB1\uDDB3-\uDDB9\uDDBB\uDDBC\uDDC0-\uDDF3\uDE00-\uDF36\uDF40-\uDF55\uDF60-\uDF67\uDF80-\uDF85\uDF87-\uDFB0\uDFB2-\uDFBA]|\uD802[\uDC00-\uDC05\uDC08\uDC0A-\uDC35\uDC37\uDC38\uDC3C\uDC3F-\uDC55\uDC58-\uDC76\uDC79-\uDC9E\uDCA7-\uDCAF\uDCE0-\uDCF2\uDCF4\uDCF5\uDCFB-\uDD1B\uDD20-\uDD39\uDD40-\uDD59\uDD80-\uDDB7\uDDBC-\uDDCF\uDDD2-\uDE00\uDE10-\uDE13\uDE15-\uDE17\uDE19-\uDE35\uDE40-\uDE48\uDE60-\uDE7E\uDE80-\uDE9F\uDEC0-\uDEC7\uDEC9-\uDEE4\uDEEB-\uDEEF\uDF00-\uDF35\uDF40-\uDF55\uDF58-\uDF72\uDF78-\uDF91\uDFA9-\uDFAF]|\uD803[\uDC00-\uDC48\uDC80-\uDCB2\uDCC0-\uDCF2\uDCFA-\uDD23\uDD30-\uDD39\uDD40-\uDD65\uDD6F-\uDD85\uDE60-\uDE7E\uDE80-\uDEA9\uDEB0\uDEB1\uDEC2-\uDEC7\uDF00-\uDF27\uDF30-\uDF45\uDF51-\uDF54\uDF70-\uDF81\uDFB0-\uDFCB\uDFE0-\uDFF6]|\uD804[\uDC03-\uDC37\uDC52-\uDC6F\uDC71\uDC72\uDC75\uDC83-\uDCAF\uDCD0-\uDCE8\uDCF0-\uDCF9\uDD03-\uDD26\uDD36-\uDD3F\uDD44\uDD47\uDD50-\uDD72\uDD76\uDD83-\uDDB2\uDDC1-\uDDC4\uDDD0-\uDDDA\uDDDC\uDDE1-\uDDF4\uDE00-\uDE11\uDE13-\uDE2B\uDE3F\uDE40\uDE80-\uDE86\uDE88\uDE8A-\uDE8D\uDE8F-\uDE9D\uDE9F-\uDEA8\uDEB0-\uDEDE\uDEF0-\uDEF9\uDF05-\uDF0C\uDF0F\uDF10\uDF13-\uDF28\uDF2A-\uDF30\uDF32\uDF33\uDF35-\uDF39\uDF3D\uDF50\uDF5D-\uDF61\uDF80-\uDF89\uDF8B\uDF8E\uDF90-\uDFB5\uDFB7\uDFD1\uDFD3]|\uD805[\uDC00-\uDC34\uDC47-\uDC4A\uDC50-\uDC59\uDC5F-\uDC61\uDC80-\uDCAF\uDCC4\uDCC5\uDCC7\uDCD0-\uDCD9\uDD80-\uDDAE\uDDD8-\uDDDB\uDE00-\uDE2F\uDE44\uDE50-\uDE59\uDE80-\uDEAA\uDEB8\uDEC0-\uDEC9\uDED0-\uDEE3\uDF00-\uDF1A\uDF30-\uDF3B\uDF40-\uDF46]|\uD806[\uDC00-\uDC2B\uDCA0-\uDCF2\uDCFF-\uDD06\uDD09\uDD0C-\uDD13\uDD15\uDD16\uDD18-\uDD2F\uDD3F\uDD41\uDD50-\uDD59\uDDA0-\uDDA7\uDDAA-\uDDD0\uDDE1\uDDE3\uDE00\uDE0B-\uDE32\uDE3A\uDE50\uDE5C-\uDE89\uDE9D\uDEB0-\uDEF8\uDFC0-\uDFE0\uDFF0-\uDFF9]|\uD807[\uDC00-\uDC08\uDC0A-\uDC2E\uDC40\uDC50-\uDC6C\uDC72-\uDC8F\uDD00-\uDD06\uDD08\uDD09\uDD0B-\uDD30\uDD46\uDD50-\uDD59\uDD60-\uDD65\uDD67\uDD68\uDD6A-\uDD89\uDD98\uDDA0-\uDDA9\uDDB0-\uDDDB\uDDE0-\uDDE9\uDEE0-\uDEF2\uDF02\uDF04-\uDF10\uDF12-\uDF33\uDF50-\uDF59\uDFB0\uDFC0-\uDFD4]|\uD808[\uDC00-\uDF99]|\uD809[\uDC00-\uDC6E\uDC80-\uDD43]|\uD80B[\uDF90-\uDFF0]|[\uD80C\uD80E\uD80F\uD81C-\uD822\uD840-\uD868\uD86A-\uD86D\uD86F-\uD872\uD874-\uD879\uD880-\uD883\uD885-\uD88C][\uDC00-\uDFFF]|\uD80D[\uDC00-\uDC2F\uDC41-\uDC46\uDC60-\uDFFF]|\uD810[\uDC00-\uDFFA]|\uD811[\uDC00-\uDE46]|\uD818[\uDD00-\uDD1D\uDD30-\uDD39]|\uD81A[\uDC00-\uDE38\uDE40-\uDE5E\uDE60-\uDE69\uDE70-\uDEBE\uDEC0-\uDEC9\uDED0-\uDEED\uDF00-\uDF2F\uDF40-\uDF43\uDF50-\uDF59\uDF5B-\uDF61\uDF63-\uDF77\uDF7D-\uDF8F]|\uD81B[\uDD40-\uDD6C\uDD70-\uDD79\uDE40-\uDE96\uDEA0-\uDEB8\uDEBB-\uDED3\uDF00-\uDF4A\uDF50\uDF93-\uDF9F\uDFE0\uDFE1\uDFE3\uDFF2-\uDFF6]|\uD823[\uDC00-\uDCD5\uDCFF-\uDD1E\uDD80-\uDDF2]|\uD82B[\uDFF0-\uDFF3\uDFF5-\uDFFB\uDFFD\uDFFE]|\uD82C[\uDC00-\uDD22\uDD32\uDD50-\uDD52\uDD55\uDD64-\uDD67\uDD70-\uDEFB]|\uD82F[\uDC00-\uDC6A\uDC70-\uDC7C\uDC80-\uDC88\uDC90-\uDC99]|\uD833[\uDCF0-\uDCF9]|\uD834[\uDEC0-\uDED3\uDEE0-\uDEF3\uDF60-\uDF78]|\uD835[\uDC00-\uDC54\uDC56-\uDC9C\uDC9E\uDC9F\uDCA2\uDCA5\uDCA6\uDCA9-\uDCAC\uDCAE-\uDCB9\uDCBB\uDCBD-\uDCC3\uDCC5-\uDD05\uDD07-\uDD0A\uDD0D-\uDD14\uDD16-\uDD1C\uDD1E-\uDD39\uDD3B-\uDD3E\uDD40-\uDD44\uDD46\uDD4A-\uDD50\uDD52-\uDEA5\uDEA8-\uDEC0\uDEC2-\uDEDA\uDEDC-\uDEFA\uDEFC-\uDF14\uDF16-\uDF34\uDF36-\uDF4E\uDF50-\uDF6E\uDF70-\uDF88\uDF8A-\uDFA8\uDFAA-\uDFC2\uDFC4-\uDFCB\uDFCE-\uDFFF]|\uD837[\uDF00-\uDF1E\uDF25-\uDF2A]|\uD838[\uDC30-\uDC6D\uDD00-\uDD2C\uDD37-\uDD3D\uDD40-\uDD49\uDD4E\uDE90-\uDEAD\uDEC0-\uDEEB\uDEF0-\uDEF9]|\uD839[\uDCD0-\uDCEB\uDCF0-\uDCF9\uDDD0-\uDDED\uDDF0-\uDDFA\uDEC0-\uDEDE\uDEE0-\uDEE2\uDEE4\uDEE5\uDEE7-\uDEED\uDEF0-\uDEF4\uDEFE\uDEFF\uDFE0-\uDFE6\uDFE8-\uDFEB\uDFED\uDFEE\uDFF0-\uDFFE]|\uD83A[\uDC00-\uDCC4\uDCC7-\uDCCF\uDD00-\uDD43\uDD4B\uDD50-\uDD59]|\uD83B[\uDC71-\uDCAB\uDCAD-\uDCAF\uDCB1-\uDCB4\uDD01-\uDD2D\uDD2F-\uDD3D\uDE00-\uDE03\uDE05-\uDE1F\uDE21\uDE22\uDE24\uDE27\uDE29-\uDE32\uDE34-\uDE37\uDE39\uDE3B\uDE42\uDE47\uDE49\uDE4B\uDE4D-\uDE4F\uDE51\uDE52\uDE54\uDE57\uDE59\uDE5B\uDE5D\uDE5F\uDE61\uDE62\uDE64\uDE67-\uDE6A\uDE6C-\uDE72\uDE74-\uDE77\uDE79-\uDE7C\uDE7E\uDE80-\uDE89\uDE8B-\uDE9B\uDEA1-\uDEA3\uDEA5-\uDEA9\uDEAB-\uDEBB]|\uD83C[\uDD00-\uDD0C]|\uD83E[\uDFF0-\uDFF9]|\uD869[\uDC00-\uDEDF\uDF00-\uDFFF]|\uD86E[\uDC00-\uDC1D\uDC20-\uDFFF]|\uD873[\uDC00-\uDEAD\uDEB0-\uDFFF]|\uD87A[\uDC00-\uDFE0\uDFF0-\uDFFF]|\uD87B[\uDC00-\uDE5D]|\uD87E[\uDC00-\uDE1D]|\uD884[\uDC00-\uDF4A\uDF50-\uDFFF]|\uD88D[\uDC00-\uDC79])*)$/.exec(before);
    if (!match) {
      this.closeAutocomplete();
      return;
    }
    const _match = _slicedToArray(match, 4),
      symbol = _match[2],
      term = _match[3];
    const type = symbol;

    // A bare `:` matches nearly the whole emoji map, and a bare `@` every user;
    // neither is a useful list, so wait for something to filter on. `@` opens at
    // once because @here/@all are worth offering immediately.
    if (type === ":" && term.length < 2) {
      this.closeAutocomplete();
      return;
    }
    this.trigger = {
      type,
      start: caret - term.length - 1,
      end: caret,
      term
    };
    if (type === ":") {
      // Custom emoji first, and on their own budget rather than competing for
      // the twelve slots. There are few of them, they are forum-specific, and a
      // member who typed `:kap` almost certainly means the one their forum
      // added — not `kappa`'s nearest Unicode neighbour.
      const custom = searchCustomEmoji(term, 4).map(entry => ({
        key: "flamoji-" + entry.name,
        insert: entry.insert,
        label: ":" + entry.name + ":",
        emoji: customEmojiImage(entry, "ChatAutocomplete-flamoji"),
        hint: entry.title !== entry.name ? entry.title : null
      }));
      const unicode = searchEmoji(term, 12).map(entry => ({
        key: "emoji-" + entry.name,
        insert: ":" + entry.name + ":",
        label: ":" + entry.name + ":",
        emoji: entry.unicode
      }));

      // A custom emoji may share a name with a Unicode one, and the formatter
      // gives the custom one precedence — so offering both would let someone
      // pick a glyph they will not get.
      const taken = new Set(custom.map(entry => entry.label));
      this.suggestions = [...custom, ...unicode.filter(entry => !taken.has(entry.label))];
      this.activeSuggestion = 0;
      if (this.suggestions.length === 0) this.closeAutocomplete();
      return;
    }
    this.suggestUsers(term);
  }
  suggestUsers(term) {
    const channelWide = [];
    if (app.forum.attribute("canMentionChatChannelWide")) {
      for (const name of ["here", "all"]) {
        if (name.startsWith(term.toLowerCase())) {
          channelWide.push({
            key: "wide-" + name,
            insert: "@" + name,
            label: "@" + name,
            icon: "fas fa-bullhorn",
            hint: app.translator.trans("ramon-chat.forum.composer.mention_".concat(name), {}, true)
          });
        }
      }
    }
    this.suggestions = channelWide;
    this.activeSuggestion = 0;
    if (this.mentionTimer !== null) window.clearTimeout(this.mentionTimer);
    if (term.length < 1) return;
    const mine = ++this.mentionSequence;
    this.mentionTimer = window.setTimeout(() => {
      app.store.find("users", {
        // Scoped to the channel: mentioning someone who is not in it notifies
        // them about a conversation they cannot open, and in a private channel
        // the unscoped list named people who cannot see it exists.
        filter: {
          q: term,
          chatChannel: Number(this.attrs.channel.id())
        },
        page: {
          limit: 6
        }
      }).then(results => {
        var _this$trigger;
        // A stale response must not replace a newer one, and must not reopen a
        // list the user has already dismissed or typed past.
        if (mine !== this.mentionSequence || ((_this$trigger = this.trigger) == null ? void 0 : _this$trigger.type) !== "@") return;
        this.suggestions = [...channelWide, ...(Array.isArray(results) ? results : []).map(user => {
          var _user$slug;
          return {
            key: "user-" + user.id(),
            insert: "@" + ((_user$slug = user.slug()) != null ? _user$slug : user.username()),
            label: user.displayName(),
            hint: "@" + user.username(),
            user
          };
        })];
        this.activeSuggestion = 0;
        m.redraw();
      }).catch(() => {
        // Keep whatever @here/@all we already have; a failed lookup is not
        // worth an error for something this incidental.
      });
    }, MENTION_DEBOUNCE);
  }
  applySuggestion(suggestion) {
    const el = this.textarea;
    const _this$attrs3 = this.attrs,
      channel = _this$attrs3.channel,
      state = _this$attrs3.state,
      threadId = _this$attrs3.threadId;
    if (!el || !this.trigger) return;
    const _this$trigger2 = this.trigger,
      start = _this$trigger2.start,
      end = _this$trigger2.end;
    const value = el.value.slice(0, start) + suggestion.insert + " " + el.value.slice(end);
    const caret = start + suggestion.insert.length + 1;
    state.setDraft(Number(channel.id()), value, threadId != null ? threadId : null);
    this.closeAutocomplete();

    // The value is redrawn from the draft, so the caret has to be restored after
    // Mithril writes it back or it jumps to the end of the text.
    m.redraw.sync();
    el.focus();
    el.setSelectionRange(caret, caret);
    this.resize();
  }
  closeAutocomplete() {
    this.trigger = null;
    this.suggestions = [];
    this.activeSuggestion = 0;
  }

  /** Whether the list is open and should own the arrow keys, Enter and Escape. */
  autocompleteOpen() {
    return this.trigger !== null && this.suggestions.length > 0;
  }
  onKeyDown(e) {
    // The suggestion list owns these keys while it is open, and must be checked
    // before anything else: Enter here means "accept", not "send", and ArrowUp
    // means "previous suggestion", not "edit my last message".
    if (this.autocompleteOpen()) {
      const count = this.suggestions.length;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        this.activeSuggestion = (this.activeSuggestion + step + count) % count;
        return;
      }

      // Ctrl/Cmd+Enter is "send", never "accept": in Ctrl+Enter mode a plain
      // Enter still belongs to the list, but the send shortcut has to reach the
      // send path or the list becomes a trap for anyone who used it.
      if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        this.applySuggestion(this.suggestions[this.activeSuggestion]);
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        this.applySuggestion(this.suggestions[this.activeSuggestion]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        this.closeAutocomplete();
        return;
      }
    }

    // Which keystroke sends is the forum's choice. Default: Enter sends and
    // Shift+Enter breaks the line, the chat convention. With
    // `send_with_ctrl_enter` on, Enter breaks the line and only Ctrl/Cmd+Enter
    // sends — for forums whose members write long messages and lose them to a
    // stray Enter. Cmd is accepted alongside Ctrl because on macOS the modifier
    // for this shortcut is Cmd, and a mac user pressing Ctrl+Enter means the
    // same thing.
    if (e.key === "Enter" && !e.altKey) {
      const withModifier = e.ctrlKey || e.metaKey;
      if (sendsOnCtrlEnter() ? withModifier : !e.shiftKey && !withModifier) {
        e.preventDefault();
        this.submit();
        return;
      }
    }
    if (e.key === "Escape") {
      this.cancelContext();
    }

    // Arrow-up on an empty composer edits your last message — a chat convention.
    if (e.key === "ArrowUp") {
      var _this$attrs$threadId4;
      const _this$attrs4 = this.attrs,
        channel = _this$attrs4.channel,
        state = _this$attrs4.state;
      const draft = state.draft(Number(channel.id()), (_this$attrs$threadId4 = this.attrs.threadId) != null ? _this$attrs$threadId4 : null);
      if (draft === "" && !this.editing()) {
        var _state$streams$Number, _state$streams$Number2;
        // The thread panel's own window when this composer is scoped to a thread,
        // so arrow-up cannot reach back into the channel from inside a thread.
        const window = this.attrs.threadId ? state.threadStream(this.attrs.threadId).messages : (_state$streams$Number = (_state$streams$Number2 = state.streams[Number(channel.id())]) == null ? void 0 : _state$streams$Number2.messages) != null ? _state$streams$Number : [];
        const mine = [...window].reverse().find(msg => msg.canEdit());
        if (mine) {
          e.preventDefault();
          this.startEditing(mine);
        }
      }
    }
  }

  /** Grows the textarea with its content, up to the CSS max-height. */
  resize() {
    const el = this.textarea;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = "".concat(el.scrollHeight, "px");
  }
  startEditing(message) {
    var _message$content, _this$textarea;
    const _this$attrs5 = this.attrs,
      channel = _this$attrs5.channel,
      state = _this$attrs5.state,
      threadId = _this$attrs5.threadId;
    state.setEditing(Number(channel.id()), message, threadId != null ? threadId : null);
    state.setDraft(Number(channel.id()), (_message$content = message.content()) != null ? _message$content : "", threadId != null ? threadId : null);
    (_this$textarea = this.textarea) == null || _this$textarea.focus();
    m.redraw();
  }
  cancelContext() {
    const _this$attrs6 = this.attrs,
      channel = _this$attrs6.channel,
      state = _this$attrs6.state,
      threadId = _this$attrs6.threadId;
    if (this.editing()) {
      state.clearDraft(Number(channel.id()), threadId != null ? threadId : null);
    }
    state.clearContext(Number(channel.id()), threadId != null ? threadId : null);
    m.redraw();
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  /**
   * Opens the sticker picker, or closes it if this button opened it.
   *
   * The chosen shortcode goes in at the caret like any other typed text, so it is
   * still editable and still part of the draft — the message is only rendered
   * into a sticker by the formatter, on send.
   */
  toggleStickers(e) {
    var _closest, _e$currentTarget;
    e.preventDefault();
    e.stopPropagation();
    if (isStickerPickerOpen()) {
      closeStickerPicker();
      return;
    }
    const trigger = (_closest = (_e$currentTarget = e.currentTarget) == null ? void 0 : _e$currentTarget.closest(".ChatComposer-tool")) != null ? _closest : null;
    openStickerPicker(trigger, text => this.insertAtCursor(text));
  }

  /**
   * Inserts text where the caret is, rather than appending.
   *
   * The composer keeps its value in the draft rather than in the DOM, so both
   * have to be updated: the textarea for the caret position the user can see, and
   * the draft for what is actually sent.
   */
  insertAtCursor(text) {
    var _this$textarea$select, _this$textarea$select2;
    const _this$attrs7 = this.attrs,
      channel = _this$attrs7.channel,
      state = _this$attrs7.state,
      threadId = _this$attrs7.threadId;
    const channelId = Number(channel.id());
    const current = state.draft(channelId, threadId != null ? threadId : null);
    if (!this.textarea) {
      state.setDraft(channelId, current + text, threadId != null ? threadId : null);
      m.redraw();
      return;
    }
    const start = (_this$textarea$select = this.textarea.selectionStart) != null ? _this$textarea$select : current.length;
    const end = (_this$textarea$select2 = this.textarea.selectionEnd) != null ? _this$textarea$select2 : start;
    const next = current.slice(0, start) + text + current.slice(end);
    state.setDraft(channelId, next, threadId != null ? threadId : null);
    m.redraw.sync();

    // Restored after the redraw, or the caret jumps to the end of the box.
    this.textarea.focus();
    this.textarea.setSelectionRange(start + text.length, start + text.length);
    this.resize();
  }

  /**
   * Starts (or refreshes) the slow-mode countdown.
   *
   * Driven by a one-second interval rather than computed on each redraw: nothing
   * else would cause a redraw while the user waits, so the number would sit
   * frozen at whatever it read when they last typed.
   *
   * The server is the authority. This only mirrors what it already told us, so a
   * reload — or a second tab — still shows the wait rather than offering a send
   * that is refused.
   */
  startCooldown(seconds) {
    this.stopCooldown();
    if (seconds <= 0) return;
    this.cooldown = seconds;
    this.cooldownTimer = window.setInterval(() => {
      this.cooldown -= 1;
      if (this.cooldown <= 0) this.stopCooldown();
      m.redraw();
    }, 1000);
  }
  stopCooldown() {
    if (this.cooldownTimer !== null) {
      window.clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    this.cooldown = 0;
  }
  async submit() {
    const _this$attrs8 = this.attrs,
      channel = _this$attrs8.channel,
      state = _this$attrs8.state,
      threadId = _this$attrs8.threadId,
      onSent = _this$attrs8.onSent;
    if (this.sending) return;
    const channelId = Number(channel.id());
    const content = state.draft(channelId, threadId != null ? threadId : null);
    if (!content.trim() && state.pendingUploads.length === 0) return;

    // Refused here rather than by the server, and without touching the draft:
    // the text stays in the box so it can be shortened instead of retyped. The
    // server checks the same limit — this only saves the round trip and gives a
    // count the API error cannot (it says "too long", not "by how much").
    const max = resolveMaxMessageLength(channel);
    if (content.length > max) {
      app.modal.show(MessageTooLongModal, {
        length: content.length,
        max
      });
      return;
    }

    // Captured before the box is disabled. Disabling a focused element blurs it,
    // and re-enabling does not hand focus back — which is why sending a message
    // meant clicking the box again before the next one could be typed. Restoring
    // it unconditionally would be wrong: someone who tabbed away, or opened the
    // emoji picker, while the request was in flight should keep the cursor where
    // they put it. `contains` covers both ways in, the textarea for an Enter and
    // the send button for a click.
    const hadFocus = Boolean(this.element && this.element.contains(document.activeElement));
    this.sending = true;

    // Clear before the request resolves so typing can continue immediately.
    state.clearDraft(channelId, threadId != null ? threadId : null);
    this.resize();
    const editing = this.editing();
    const replyingTo = this.replyingTo();
    try {
      if (editing) {
        const payload = await app.request({
          method: "PATCH",
          url: "".concat(app.forum.attribute("apiUrl"), "/chat-messages/").concat(editing.id()),
          body: {
            data: {
              attributes: {
                content
              }
            }
          }
        });
        app.store.pushPayload(payload);
        state.setEditing(channelId, null, threadId != null ? threadId : null);
      } else {
        await state.send(channelId, content, {
          threadId: threadId != null ? threadId : null,
          replyToId: replyingTo ? Number(replyingTo.id()) : null,
          // Only when the reply was staged by "reply in thread". This used to be
          // inferred from the reply being branchable at all, which made every
          // ordinary reply in a threading-enabled channel open a thread and left
          // no way to simply reply.
          //
          // The rest still guards it: only from the channel composer, and only
          // when the target can actually be branched — `createThread` inside a
          // thread would ask for a nested one, which the policy refuses.
          createThread: state.branchingFrom(channelId, threadId != null ? threadId : null) && Boolean(replyingTo) && Boolean(channel.threadingEnabled()) && !threadId && Boolean(replyingTo == null ? void 0 : replyingTo.canCreateThread())
        });
      }

      // The channel's own cooldown, restarted from what the send just consumed.
      // Editing an existing message does not: slow mode is about how often you
      // speak, not how often you correct yourself.
      //
      // `slowModeRemaining` is exemption-aware and so covers the load path, but
      // this one reads the channel's raw setting — without the same check, a
      // holder of `bypassSlowMode` sends once and is then locked out for the full
      // window by the interface alone, while the server would have taken the next
      // message happily.
      if (!editing && !app.forum.attribute("canBypassChatSlowMode")) {
        var _channel$slowModeSeco2;
        this.startCooldown(Number((_channel$slowModeSeco2 = channel.slowModeSeconds()) != null ? _channel$slowModeSeco2 : 0));
      }
      onSent == null || onSent();
    } catch (e) {
      var _e$response2;
      // Hand the text back rather than losing it, and surface why.
      state.setDraft(channelId, content, threadId != null ? threadId : null);
      const detail = e == null || (_e$response2 = e.response) == null || (_e$response2 = _e$response2.errors) == null || (_e$response2 = _e$response2[0]) == null ? void 0 : _e$response2.detail;
      app.alerts.show({
        type: "error"
      }, detail != null ? detail : app.translator.trans("ramon-chat.forum.composer.send_failed", {}, true));
    } finally {
      var _this$textarea2;
      this.sending = false;

      // Sync, so the textarea is enabled again by the time focus is asked for —
      // calling focus() on a still-disabled element does nothing at all.
      m.redraw.sync();
      if (hadFocus) (_this$textarea2 = this.textarea) == null || _this$textarea2.focus();
    }
  }

  // ── Uploads ────────────────────────────────────────────────────────────────

  pickFiles() {
    var _this$element$querySe;
    (_this$element$querySe = this.element.querySelector(".ChatComposer-fileInput")) == null || _this$element$querySe.click();
  }
  async onFilesPicked(e) {
    var _input$files;
    const input = e.target;
    const files = Array.from((_input$files = input.files) != null ? _input$files : []);

    // Reset immediately so picking the same file twice still fires a change.
    input.value = "";
    if (files.length === 0) return;
    this.uploading = true;
    m.redraw();
    for (const file of files) {
      try {
        const body = new FormData();
        body.append("file", file);
        // Where this is going to be posted. The server uses it to put a file
        // meant for a private channel on the private disk from the start, rather
        // than on the public one and moving it on send. Sending still enforces
        // the rule — this is the hint, not the gate.
        body.append("channelId", String(this.attrs.channel.id()));
        const payload = await app.request({
          method: "POST",
          url: "".concat(app.forum.attribute("apiUrl"), "/chat/uploads"),
          body,
          serialize: raw => raw
        });
        const upload = app.store.pushPayload(payload);
        this.attrs.state.pendingUploads.push(upload);
      } catch (err) {
        var _err$response;
        const detail = err == null || (_err$response = err.response) == null || (_err$response = _err$response.errors) == null || (_err$response = _err$response[0]) == null ? void 0 : _err$response.detail;
        app.alerts.show({
          type: "error"
        }, detail != null ? detail : app.translator.trans("ramon-chat.forum.composer.upload_failed", {}, true));
      }
    }
    this.uploading = false;
    m.redraw();
  }
  removeUpload(id) {
    const state = this.attrs.state;
    state.pendingUploads = state.pendingUploads.filter(u => Number(u.id()) !== id);

    // Discard it server-side too, so a cancelled attachment does not wait for the
    // retention sweep.
    app.request({
      method: "DELETE",
      url: "".concat(app.forum.attribute("apiUrl"), "/chat-uploads/").concat(id)
    }).catch(() => {});
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChatComposer', ChatComposer);